import { useMutation, useQuery } from "@apollo/client";
import { logger } from "@clockwise/client-commons/src/util/logger";
import {
  EventTable,
  EventTableHeading,
  EventTableRow,
} from "@clockwise/web-commons/src/components/EventTable/";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import React, { useEffect, useRef, useState } from "react";
import { throttle } from "throttle-debounce";
import { SetCalendarEventFlexibilityDocument } from "../../graphql/__generated__/SetCalendarEventFlexibility.generated";
import getNextOccuranceText from "../../util/getNextOccuranceText";
import { getCurrentOrg } from "../../util/org.util";
import useMessageAndSeverity from "../hooks/useMessageAndSeverity";
import { OneOffHoldsDocument } from "./graphql/__generated__/OneOffHolds.generated";
import { OneOffHold } from "./types";
import parseOneOffHoldsGQL from "./utils/parseOneOffHoldsGQL";

const trackingSourceId = "One-off holds control";

const OneOffHoldsControl = () => {
  const [suggestions, setSuggestions] = useState<OneOffHold[]>([]);
  const { setInfo, setError } = useMessageAndSeverity();
  const trackEvent = useTracking();
  const { error, loading, data } = useQuery(OneOffHoldsDocument);
  const [setFlexibility] = useMutation(SetCalendarEventFlexibilityDocument);

  const throttledSetFlexiblility = useRef(throttle(250, setFlexibility)).current;

  useEffect(() => {
    if (data) {
      setSuggestions(parseOneOffHoldsGQL(data));
    }
  }, [data]);

  const updateSuggestionFlexibility = (id: string, flexible: boolean) => {
    setSuggestions((state) => {
      return state.map((suggestion) => {
        if (suggestion.id === id) {
          return Object.assign({}, suggestion, { flexible });
        }

        return suggestion;
      });
    });
  };

  const org = getCurrentOrg(data?.viewer);
  const orgId = org?.id || "";

  const handleFlexibilityChangeSuccess = (eventId: string, isFlexible: boolean) => {
    setInfo("Successfully updated flexibility");
    trackEvent(TrackingEvents.FLEXIBLE_HOLD.UPDATE_FLEXIBILITY, {
      isFlexible,
      eventId,
      source: trackingSourceId,
    });
  };

  const handleFlexibilityChangeError = () => {
    setError("Unable to update flexibility");
    logger.error("Failed to update flexibility");
  };

  const handleFlexibilityChange = (eventId: string, isFlexible: boolean) => {
    const eventsToEnable = [];
    const eventsToDisable = [];

    if (isFlexible) {
      eventsToEnable.push(eventId);
    } else {
      eventsToDisable.push(eventId);
    }

    // optimistically update UI
    updateSuggestionFlexibility(eventId, isFlexible);

    void throttledSetFlexiblility({
      variables: { input: { orgId, eventsToEnable, eventsToDisable } },
      onCompleted: () => handleFlexibilityChangeSuccess(eventId, isFlexible),
      onError: () => {
        handleFlexibilityChangeError();
        updateSuggestionFlexibility(eventId, !isFlexible);
      },
    });
  };

  const eventTableError = error?.message
    ? "Unable to fetch this data for One-off tasks"
    : undefined;

  return (
    <>
      <div className="cw-mb-3">
        <EventTableHeading headingLevel="h2" heading="One-off tasks" />
      </div>
      <div>
        <EventTable
          emptyText="No holds to display"
          errorText={eventTableError}
          loading={loading}
          onFlexibilityChange={handleFlexibilityChange}
          showAvatars
        >
          {suggestions.map(({ id, title, startTime, flexible }) => (
            <EventTableRow
              eventId={id}
              flexible={flexible}
              key={id}
              nextOccruance={startTime ? getNextOccuranceText(startTime) : "Unknown Date"}
              title={title}
            />
          ))}
        </EventTable>
      </div>
    </>
  );
};

export default OneOffHoldsControl;
